<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div
    class="block-editor"
    :class="{
      selected: isSelected,
      overlapping: hasOverlap,
    }"
    :style="{
      gridColumn: `${block.gridPosition.colStart} / ${block.gridPosition.colEnd}`,
      gridRow: `${block.gridPosition.rowStart} / ${block.gridPosition.rowEnd}`,
    }"
    :draggable="true"
    @dragstart="$emit('dragstart', $event, block)"
    @dragend="$emit('dragend', $event)"
    @click="$emit('click', block)"
  >
    <div class="block-header">
      <span class="block-title">
        {{ block.title || block.type }}
      </span>

      <!-- Full actions for larger blocks -->
      <div v-if="!isSmallBlock" class="block-actions">
        <PrimeButton
          icon="ri-inbox-unarchive-line"
          text
          rounded
          size="small"
          @click.stop="$emit('move-to-basket', block)"
          v-tooltip.top="$t('dashboardAdmin.moveToBasket')"
        />
        <PrimeButton
          icon="ri-file-copy-line"
          text
          rounded
          size="small"
          @click.stop="$emit('duplicate', block)"
          v-tooltip.top="$t('dashboardAdmin.duplicateBlock')"
        />
        <PrimeButton
          icon="ri-pencil-line"
          text
          rounded
          size="small"
          @click.stop="$emit('edit', block)"
          v-tooltip.top="$t('dashboardAdmin.editBlock')"
        />
        <PrimeButton
          icon="ri-delete-bin-line"
          text
          rounded
          size="small"
          severity="danger"
          @click.stop="$emit('delete', block)"
          v-tooltip.top="$t('dashboardAdmin.deleteBlock')"
        />
      </div>

      <!-- Menu for small blocks -->
      <div v-else class="block-actions">
        <PrimeButton
          icon="ri-more-2-fill"
          text
          rounded
          size="small"
          @click.stop="toggleMenu"
          aria-haspopup="true"
          aria-controls="block_menu"
        />
        <PrimeMenu
          ref="menu"
          id="block_menu"
          :model="menuItems"
          :popup="true"
        />
      </div>
    </div>
    <div class="block-content">
      <div class="block-type-badge">{{ block.type }}</div>
    </div>

    <!-- Resize handles -->
    <div
      class="resize-handle resize-handle-right"
      @mousedown="$emit('resize-start', $event, block, 'right')"
    />
    <div
      class="resize-handle resize-handle-bottom"
      @mousedown="$emit('resize-start', $event, block, 'bottom')"
    />
    <div
      class="resize-handle resize-handle-corner"
      @mousedown="$emit('resize-start', $event, block, 'corner')"
    />
  </div>
</template>

<script>
import Button from "primevue/button";
import Menu from "primevue/menu";
import Tooltip from "primevue/tooltip";

export default {
  name: "GridBlock",
  components: {
    PrimeButton: Button,
    PrimeMenu: Menu,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    block: {
      type: Object,
      required: true,
    },
    gridSettings: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    hasOverlap: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "dragstart",
    "dragend",
    "click",
    "move-to-basket",
    "duplicate",
    "edit",
    "delete",
    "resize-start",
  ],
  computed: {
    isSmallBlock() {
      const width =
        this.block.gridPosition.colEnd - this.block.gridPosition.colStart;

      // Calculate relative width as percentage of total grid width
      const relativeWidth = width / this.gridSettings.columns;

      // Consider block small if it occupies less than 30% of grid width
      return relativeWidth < 0.3;
    },
    menuItems() {
      return [
        {
          label: this.$t("dashboardAdmin.moveToBasket"),
          icon: "ri-inbox-unarchive-line",
          command: () => {
            this.$emit("move-to-basket", this.block);
          },
        },
        {
          label: this.$t("dashboardAdmin.duplicateBlock"),
          icon: "ri-file-copy-line",
          command: () => {
            this.$emit("duplicate", this.block);
          },
        },
        {
          label: this.$t("dashboardAdmin.editBlock"),
          icon: "ri-pencil-line",
          command: () => {
            this.$emit("edit", this.block);
          },
        },
        {
          label: this.$t("dashboardAdmin.deleteBlock"),
          icon: "ri-delete-bin-line",
          class: "p-error",
          command: () => {
            this.$emit("delete", this.block);
          },
        },
      ];
    },
  },
  methods: {
    toggleMenu(event) {
      this.$refs.menu.toggle(event);
    },
  },
};
</script>

<style scoped>
.block-editor {
  background: var(--p-primary-50);
  border: 2px solid var(--p-primary-200);
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: move;
  position: relative;
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
}

.block-editor:hover {
  border-color: var(--p-primary-500);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.block-editor.selected {
  border-color: var(--p-primary-500);
  border-width: 3px;
  box-shadow: 0 0 0 3px var(--p-primary-100);
}

.block-editor.overlapping {
  background: var(--p-red-50);
  border-color: var(--p-red-500);
  box-shadow: 0 0 0 2px var(--p-red-100);
}

.block-editor.overlapping .block-type-badge {
  background: var(--p-red-100);
  color: var(--p-red-700);
}

.block-editor.overlapping .block-title {
  color: var(--p-red-700);
}

.block-editor.dragging {
  opacity: 0.5;
  transform: scale(0.95);
  filter: brightness(0.9);
  transition: all 0.2s ease;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.block-title {
  font-weight: 600;
  color: var(--p-primary-700);
}

.block-actions {
  display: flex;
  gap: 0.25rem;
}

.block-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.block-type-badge {
  background: var(--p-primary-100);
  color: var(--p-primary-700);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-block;
  width: fit-content;
}

/* Resize handles */
.resize-handle {
  position: absolute;
  background: var(--p-primary-500);
  z-index: 10;
}

.resize-handle-right {
  right: -4px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 40px;
  cursor: ew-resize;
  border-radius: 4px;
}

.resize-handle-bottom {
  bottom: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 8px;
  cursor: ns-resize;
  border-radius: 4px;
}

.resize-handle-corner {
  right: -4px;
  bottom: -4px;
  width: 12px;
  height: 12px;
  cursor: nwse-resize;
  border-radius: 2px;
}
</style>
